/**
 * User Service
 * Handles user-related API calls including user profile and onboarding
 */

import { apiClient } from './api.client';
import { UserResponseDto, OnboardUserDto } from '../types/api.types';

/**
 * Get user by ID
 */
export const getUserById = async (userId: string): Promise<UserResponseDto> => {
  if (!userId || userId.trim() === '') {
    throw new Error('User ID is required and cannot be empty');
  }
  return apiClient.get<UserResponseDto>(`/api/users/${userId}`);
};

/**
 * Onboard a new user
 */
export const onboardUser = async (userData: OnboardUserDto): Promise<UserResponseDto> => {
  return apiClient.post<UserResponseDto>('/api/users/onboard', userData);
};

/**
 * Check if user exists and is onboarded
 */
export const checkUserStatus = async (userId: string): Promise<{ exists: boolean; onboarded: boolean }> => {
  try {
    const user = await getUserById(userId);
    return {
      exists: true,
      onboarded: user.onboarded,
    };
  } catch (error: any) {
    if (error.statusCode === 404) {
      return {
        exists: false,
        onboarded: false,
      };
    }
    throw error;
  }
};

/**
 * Deduct one credit from user's balance
 * Returns the new credit balance
 */
export const deductCredit = async (userId: string): Promise<number> => {
  const response = await apiClient.post<{ success: boolean; newBalance: number }>(
    '/api/users/deduct-credit',
    { userId }
  );
  return response.newBalance;
};

